import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUser from 'src/hook/data/useUser';
import { useComponentModal } from 'src/hook/modal/useComponentModal';
import usePopUp from 'src/hook/modal/usePopUp';
import { Actions } from 'src/model/enum/enums';
import useProgress from '../../../../hook/data/useProgress';
import { Word } from '../../../../model/Word';
import styles from './ReviewModal.module.scss';

interface ReviewModalProps {
    word: Word;
    reviewCount: number;
}

const ReviewModal = ({ word, reviewCount }: ReviewModalProps) => {
    const { t } = useTranslation();
    const [selectedReason, setSelectedReason] = useState('');
    const { saveProgress } = useProgress();
    const { startClosing } = useComponentModal();
    const { hide } = usePopUp();
    const { ensureCasing } = useUser();


    // Define review options with their conditions
    const reviewOptions = [
        { value: 'tomorrow', label: t(`Tomorrow${reviewCount === 1 ? ' (recommended)' : ''}`), show: true },
        { value: '2days', label: t('In 2 days(recommended)'), show: reviewCount === 2 },
        { value: '3days', label: t('In 3 days(recommended)'), show: reviewCount === 3 },
        { value: '4days', label: t('In 4 days(recommended)'), show: reviewCount === 4 },
        { value: '1week', label: t('In a week'), show: true },
        { value: '2weeks', label: t('In 2 weeks'), show: reviewCount === 6 },
        { value: '1month', label: t('In a month'), show: true },
        { value: '2months', label: t('In 2 months'), show: reviewCount === 8 },
        { value: '3months', label: t('In 3 months'), show: reviewCount === 9 },
        { value: '6months', label: t('Review in 6 months'), show: reviewCount === 10 || reviewCount === 11 || reviewCount === 12 },
        { value: 'never', label: t("Never, I won't forget it"), show: true },
    ];

    const handleContinue = () => {
        // Get the initial default value based on reviewCount
        let defaultValue = 'tomorrow';
        if (reviewCount === 2) defaultValue = '2days';
        else if (reviewCount === 3) defaultValue = '3days';
        else if (reviewCount === 4) defaultValue = '4days';
        else if (reviewCount === 5) defaultValue = '1week';
        else if (reviewCount === 6) defaultValue = '2weeks';
        else if (reviewCount === 7) defaultValue = '1month';
        else if (reviewCount === 8) defaultValue = '2months';
        else if (reviewCount === 9) defaultValue = '3months';
        else if (reviewCount === 10 || reviewCount === 11 || reviewCount === 12) defaultValue = '6months';

        // Only send to server if user changed the selection
        if (selectedReason !== defaultValue) {
            if (selectedReason === 'never') {
                saveProgress({
                    Action: Actions.Knew,
                    Value: word.ID.toString()
                });
            } else {
                // Convert selection to days
                const daysMap: { [key: string]: number } = {
                    'tomorrow': 1,
                    '2days': 2,
                    '3days': 3,
                    '4days': 4,
                    '1week': 5,
                    '2weeks': 6,
                    '1month': 7,
                    '2months': 8,
                    '3months': 9,
                    '6months': 10
                };

                const days = daysMap[selectedReason];
                saveProgress({
                    Action: Actions.ScheduleReview,
                    Value: `${word.ID}-${days}`
                });
            }
        }

        hide();
        startClosing();
    };


    useEffect(() => {
        // Set initial selected reason based on reviewCount
        if (reviewCount === 0 || reviewCount === 1) {
            setSelectedReason('tomorrow');
        } else if (reviewCount === 2) {
            setSelectedReason('2days');
        } else if (reviewCount === 3) {
            setSelectedReason('3days');
        } else if (reviewCount === 4) {
            setSelectedReason('4days');
        } else if (reviewCount === 5) {
            setSelectedReason('1week');
        } else if (reviewCount === 6) {
            setSelectedReason('2weeks');
        } else if (reviewCount === 7) {
            setSelectedReason('1month');
        } else if (reviewCount === 8) {
            setSelectedReason('2months');
        } else if (reviewCount === 9) {
            setSelectedReason('3months');
        } else if (reviewCount === 10 || reviewCount === 11 || reviewCount === 12) {
            setSelectedReason('6months');
        }
        else {
            setSelectedReason('tomorrow'); // default case
        }
    }, [reviewCount]);

    return (
        <div className={styles.reviewModal}>
            <h1>{ensureCasing(word.Text)}</h1>
            <p>{t(`Total remembered: ${reviewCount === 1 ? 'once' : reviewCount === 2 ? 'twice' : `${reviewCount} times`}`)}</p>
            <div className={styles.radioGroup}>
                <p>{t('Next review:')}</p>
                {reviewOptions
                    .filter(option => option.show)
                    .map(option => (
                        <label key={option.value}>
                            <input
                                type="radio"
                                name="reviewTime"
                                value={option.value}
                                checked={selectedReason === option.value}
                                onChange={(e) => setSelectedReason(e.target.value)}
                                className={styles.radioInput}
                            />
                            {option.label}
                        </label>
                    ))}
            </div>
            <button onClick={handleContinue}>{t('Continue')}</button>
        </div>
    );
};

export default ReviewModal;